const GCS_SCOPE = 'https://www.googleapis.com/auth/devstorage.read_write';

const base64url = (input) => {
  const bytes = typeof input === 'string'
    ? new TextEncoder().encode(input)
    : new Uint8Array(input);

  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const importPrivateKey = async (pem) => {
  const pemContents = pem
    .replace(/-----BEGIN PRIVATE KEY-----/g, '')
    .replace(/-----END PRIVATE KEY-----/g, '')
    .replace(/\s/g, '');

  const binary = Uint8Array.from(atob(pemContents), (char) => char.charCodeAt(0));

  return crypto.subtle.importKey(
    'pkcs8',
    binary,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['sign']
  );
};

const createSignedJwt = async (clientEmail, privateKeyPem) => {
  const key = await importPrivateKey(privateKeyPem);
  const header = base64url(JSON.stringify({ alg: 'RS256', typ: 'JWT' }));
  const now = Math.floor(Date.now() / 1000);
  const payload = base64url(
    JSON.stringify({
      iss: clientEmail,
      scope: GCS_SCOPE,
      aud: 'https://oauth2.googleapis.com/token',
      exp: now + 3600,
      iat: now,
    })
  );

  const unsigned = `${header}.${payload}`;
  const signature = await crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    key,
    new TextEncoder().encode(unsigned)
  );

  return `${unsigned}.${base64url(signature)}`;
};

let cachedToken = null;
let tokenExpiresAt = 0;

export const getGcsAccessToken = async () => {
  const clientEmail = process.env.REACT_APP_GCS_CLIENT_EMAIL;
  const privateKey = process.env.REACT_APP_GCS_PRIVATE_KEY?.replace(/\\n/g, '\n');

  if (!clientEmail || !privateKey) {
    throw new Error('GCS credentials are not configured. Set REACT_APP_GCS_CLIENT_EMAIL and REACT_APP_GCS_PRIVATE_KEY in .env');
  }

  if (cachedToken && Date.now() < tokenExpiresAt - 60_000) {
    return cachedToken;
  }

  const assertion = await createSignedJwt(clientEmail, privateKey);
  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      assertion,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to authenticate with Google Cloud Storage');
  }

  const { access_token: accessToken, expires_in: expiresIn } = await response.json();
  cachedToken = accessToken;
  tokenExpiresAt = Date.now() + expiresIn * 1000;

  return accessToken;
};

export const getGcsBucket = () => {
  const bucket = process.env.REACT_APP_GCS_BUCKET;
  if (!bucket) {
    throw new Error('GCS bucket is not configured. Set REACT_APP_GCS_BUCKET in .env');
  }
  return bucket;
};
